import { Stack } from '@mui/material';
import { inferParserType } from 'nuqs';
import { memo } from 'react';
import { Writable } from 'type-fest';
import FilterChipButton from '@/components/Filters/FilterChipButton';
import { FilterByValue, FromArray } from '../../../utils/type-helpers';
import Collapsible from '../../common/Collapsible';
import { BuildingFilters } from '../BuildingFilters';
import { useBuildingFilters } from '../BuildingFiltersProvider';
import FilterValueLabel from '../FilterValueLabel';
import { BUILDING_FILTERS_SEARCH_PARAMS, BuildingFiltersSearchParams } from '../search-params';

type ChipSearchParamKeys = Extract<BuildingFiltersSearchParams, 'efficiencyClasses' | 'taxBrackets'>;
type ChipSearchParams = FilterByValue<Writable<(typeof BuildingFilters)[number]>, 'showAs', 'chip'>;
export type ChipSearchParamsArray = ChipSearchParams[];

type Props<Key extends ChipSearchParamKeys> = {
  title: React.ReactNode;
  type: Key;
  options: inferParserType<typeof BUILDING_FILTERS_SEARCH_PARAMS>[Key];
  selectedOptions: inferParserType<typeof BUILDING_FILTERS_SEARCH_PARAMS>[Key];
};

const ChipFilter = memo(function ChipFilter<Key extends ChipSearchParamKeys>({
  title,
  type,
  options,
  selectedOptions,
}: Props<Key>) {
  const { setFilters } = useBuildingFilters();

  if (!options || options.length < 2) {
    return null;
  }

  const handleSelect = (
    value: FromArray<NonNullable<inferParserType<typeof BUILDING_FILTERS_SEARCH_PARAMS>[Key]>>,
    isChecked: boolean,
  ) => {
    setFilters((prev) => {
      const prevValues = prev[type] ?? [];
      let nextValues;

      if (!isChecked) {
        nextValues = [...prevValues, value];
      } else {
        nextValues = prevValues.filter((it) => it !== value);
      }
      return { ...prev, [type]: nextValues.length === 0 ? null : nextValues };
    });
  };

  return (
    <Collapsible title={title}>
      <Stack direction="row" flexWrap="wrap" gap={1} width={'100%'}>
        {options.map((item, index) => {
          const isChecked = selectedOptions?.some((it) => it === item) ?? false;

          return (
            <FilterChipButton
              key={index}
              isChecked={isChecked}
              onClick={() => handleSelect(item, isChecked)}
              label={<FilterValueLabel type={type} value={item} />}
            />
          );
        })}
      </Stack>
    </Collapsible>
  );
});

export default ChipFilter;
